import { Component, ViewChild } from '@angular/core';
import { Invoice } from '../../models/invoices/invoice';
import { InvoiceService } from '../../models/invoices/invoiceService';
import { Merchant } from '../../models/merchant/merchant';
import { Customer } from '../../models/customer/customer';
import { UIHelpers } from '../../core/ui-helpers';
import { ActivatedRoute, Router } from '@angular/router';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { MerchantService } from '../../models/merchant/merchantService';
import { CustomerService } from '../../models/customer/customerService';
import { PaymentMethod } from '../../models/paymentMethod/paymentMethod';
import { TerminalBillingFormComponent } from '../terminal/terminal-billing-form/terminal-billing-form.component';
import { TransactionResponse } from '../../models/transactions/transactionResponse';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TerminalTransactionRequest } from '../../models/orders/terminalTransactionRequest';
import { TransactionService } from '../../models/transactions/transactionService';
import { PayableInvoice } from '../../models/invoices/payableInvoice';
import { RecurringSchedule } from '../../models/recurringPayments/recurringSchedule';
import { StreetAddress } from '../../models/streetAddress/streetAddress';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { L2L3Item } from '../../models/orders/L2L3Item';

@Component({
  selector: 'app-payable-invoice',
  templateUrl: './payable-invoice.component.html',
  standalone: false,
})
export class PayableInvoiceComponent {
  paymentTypeOptions: ListItem[] = [];
  selectedMode: number = 1;

  invoice: Invoice = Invoice.createEmpty();
  payableInvoice: PayableInvoice = PayableInvoice.createEmpty();
  merchant: Merchant = Merchant.createEmpty();
  customer: Customer = Customer.createEmpty() as Customer;
  currentDate: string = new Date().toLocaleDateString();
  invoiceId: string | undefined;
  totalDueToday: number = 0;
  recurringSchedule: RecurringSchedule | undefined;
  billingAddress: Partial<StreetAddress> = StreetAddress.createEmpty();
  invoicePaid: boolean = false;

  selectedPaymentMethod: Partial<PaymentMethod> | undefined;
  paymentMethodId: string | undefined;

  tokenizeInterval: number | undefined;
  transactionResponse: TransactionResponse | undefined;
  formIsBeingSubmitted: boolean = true;

  achEnabled: boolean = false;

  loadingSpinnerRef: MatDialogRef<LoadingSpinnerComponent> | undefined;

  MODE_CREDIT_CARD = 1;
  MODE_ECHECK = 2;

  formValidated: boolean = false;

  @ViewChild(TerminalBillingFormComponent) billingForm:
    | TerminalBillingFormComponent
    | undefined;

  constructor(
    private invoiceService: InvoiceService,
    public uiHelpers: UIHelpers,
    private route: ActivatedRoute,
    private uiErrorHandler: UIErrorHandler,
    public loadingSpinnerDialog: MatDialog,
    private router: Router,
    private transactionService: TransactionService,
  ) {}

  ngOnInit() {
    this.invoiceId = this.route.snapshot.queryParamMap.get('id') as string;
    if (this.invoiceId) {
      this.invoiceService
        .getPayableInvoice(this.invoiceId)
        .then((payableInvoice) => {
          this.payableInvoice = payableInvoice;
          this.paymentTypeOptions.push(new ListItem({ id: this.MODE_CREDIT_CARD.toString(), name: 'Credit Card' }));
          if (this.payableInvoice.achEnabled) {
            this.paymentTypeOptions.push(new ListItem({ id: this.MODE_ECHECK.toString(), name: 'Electronic Check (ACH)' }));
          }
          this.payableInvoice.dueDate = new Date(this.payableInvoice.dueDate);
          this.totalDueToday = this.uiHelpers.demagnifyAmount(
            this.payableInvoice.amtDue / this.payableInvoice.remainingPayments,
            100,
          );
          if (this.payableInvoice.status == 'paid') {
            this.formIsBeingSubmitted = true;
            this.invoicePaid = true;
          } else {
            this.formIsBeingSubmitted = false;
          }
        })
        .catch((error) => {
          this.uiErrorHandler.handleError(error);
        });
    }
  }

  handleTokenizeFormValidationChanged(data: any) {
    if (data.formPassedValidation) {
      this.formValidated = true;
    } else {
      this.formValidated = false;
    }
  }

  handleTokenized(paymentMethodId: string) {
    this.paymentMethodId = paymentMethodId;
    this.selectedPaymentMethod = PaymentMethod.createEmpty();
    this.selectedPaymentMethod.id = paymentMethodId;
  }

  handlePaymentTypeChanged() {
    this.billingForm!.selectedPaymentType = this.selectedMode;
    this.billingForm!.refresh();
    this.billingForm!.loadPaymentMethods();
  }

  handleSelectedPaymentMethodChanged(paymentMethod: Partial<PaymentMethod>) {
    if (paymentMethod) {
      this.selectedPaymentMethod = paymentMethod;
      this.paymentMethodId = paymentMethod.id;
      this.billingForm?.clearSelection();
      this.formValidated = true;
    } else {
      this.formValidated = false;
    }
  }

  handleSubmitPaymnetClicked() {

    this.formIsBeingSubmitted = true;

    this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
      LoadingSpinnerComponent,
      {
        disableClose: true,
        panelClass: 'transparent-dialog',
        data: {
          mode: 'indeterminate',
        },
      },
    );

    if (!this.selectedPaymentMethod || !this.selectedPaymentMethod.id) {
      this.billingForm?.tokenize();

      let that = this;

      this.tokenizeInterval = window.setInterval(() => {
        // wait for tokenization response...
        console.log("Waiting for tokenization response...");
        if (that.paymentMethodId) {
          that.selectedPaymentMethod = PaymentMethod.createEmpty();
          that.selectedPaymentMethod.id = that.paymentMethodId;

          //If we are doing ACH we need to add the paymentmethod type
          if (this.selectedMode == this.MODE_ECHECK) {
            that.selectedPaymentMethod.cardType = 'Other';
          }

          if (that.tokenizeInterval) {
            window.clearInterval(that.tokenizeInterval);
          }

          console.log("Submitting transaction...");

          that
            ._submitTransaction()
            .then((response) => {
              this.invoiceService
                .postPayableInvoice(
                  this.invoiceId!,
                  this.uiHelpers.magnifyAmount(this.totalDueToday, 100),
                  this.payableInvoice.remainingPayments - 1,
                  response.agreementId!,
                )
                .then((success) => {
                  this.loadingSpinnerRef?.close();
                  this.transactionResponse = response;
                  window.location.reload();
                })
                .catch((error) => {
                  this.loadingSpinnerRef?.close();
                  this.formIsBeingSubmitted = false;
                  this.uiErrorHandler.handleError(error);
                });
            })
            .catch((message) => {
              this.loadingSpinnerRef?.close();
              this.uiErrorHandler.handleError(message);
              that.formIsBeingSubmitted = false;
            });
        }
      }, 1000);
    } else {
      this._submitTransaction()
        .then((response) => {
          if (response) {
            if (!response.success) {
              this.loadingSpinnerRef?.close();
              this.formIsBeingSubmitted = false;
              this.uiErrorHandler.handleError(
                'The transaction could not be processed: ' + response.error,
              );
              return;
            } else {
              this.invoiceService
                .postPayableInvoice(
                  this.invoiceId!,
                  this.uiHelpers.magnifyAmount(this.totalDueToday, 100),
                  this.payableInvoice.remainingPayments - 1,
                  response.agreementId!,
                )
                .then((success) => {
                  this.loadingSpinnerRef?.close();
                  this.transactionResponse = response;
                  window.location.reload();
                })
                .catch((error) => {
                  this.loadingSpinnerRef?.close();
                  this.formIsBeingSubmitted = false;
                  this.uiErrorHandler.handleError(error);
                });
            }
          } else {
            this.loadingSpinnerRef?.close();
            this.uiErrorHandler.handleError(
              'An error occurred while processing this transaction. Please try again later.',
            );
            this.formIsBeingSubmitted = false;
            return;
          }
        })
        .catch((ex) => {
          this.loadingSpinnerRef?.close();
          this.uiErrorHandler.handleError(
            'The transaction could not be processed: ' + ex,
          );
          this.formIsBeingSubmitted = false;
        });
    }
  }

  async _submitTransaction(): Promise<TransactionResponse> {
    this.formIsBeingSubmitted = true;

    if (this.selectedPaymentMethod) {
      //For now we are not supporting L2L3 data with invoicing.
      let items: L2L3Item[] = [];
      for (const item of this.payableInvoice.invoiceItems) {
        items.push(new L2L3Item({
          quantity: item.quantity,
          unitOfMeasure: 'ITM',
          productCode: item.id,
          unitPrice: item.unitPrice,
        }));
      }

      let transRequest: TerminalTransactionRequest =
        new TerminalTransactionRequest({
          paymentMethodId: this.selectedPaymentMethod.id,
          agreementId: this.payableInvoice.agreementId || undefined,
          customerId: this.payableInvoice.customerId,
          magnifiedAmount: this.uiHelpers.magnifyAmount(
            this.totalDueToday,
            100,
          ),
          currencyCode: 'USD',
          verb: 'AUTHCAPTURE',
          billingAddress: this.billingAddress,
          shippingAddress: this.billingAddress,
          description: this.payableInvoice.memo,
          recipientEmail: this.payableInvoice.customerEmail,
          quantity:
            this.payableInvoice.invoiceItems.reduce(
              (acc, item) => acc + item.quantity,
              0,
            ) || 1,
          unitOfMeasure: 'ITM',
          productCode: 'INV',
          ShippingAndHandlingAmount: 0,
          taxes: [],
          DutyAmount: 0,
          taxAmount: this.payableInvoice.taxMagnified,
          invoiceNumber: this.payableInvoice.number.toString(),
          poNumber: '',
          items: items,
        });

      let response: TransactionResponse | undefined;

      if (this.payableInvoice.isInstallment) {
        transRequest.verb = 'INSTALL';
        //Extend the expiry date to 100 years from now. We don't want the installment to expire before it has been paid off.
        const extendedExpiryDate = new Date(this.payableInvoice.dueDate);
        extendedExpiryDate.setFullYear(extendedExpiryDate.getFullYear() + 100);

        if (this.payableInvoice.totalAmtDue == this.payableInvoice.amtDue) {
          //This is the first payment
          this.recurringSchedule = new RecurringSchedule({
            period: 'day',
            periodCount: 0,
            chargeUntil: 'occurrences',
            numberOfCharges: this.payableInvoice.remainingPayments,
            firstChargeDate: new Date().toISOString(),
            firstChargeDateISO: new Date().toISOString(),
            finalChargeDate: extendedExpiryDate.toISOString(),
            finalChargeDateISO: extendedExpiryDate.toISOString(),
          });
        } else {
          //This is a subsequent payment, so we update the fisrt charge date to be BEFORE today, this will tell the gateway that it's not an initial charge.
          const firstChargeDate = new Date();
          firstChargeDate.setDate(firstChargeDate.getDate() + 1);
          this.recurringSchedule = new RecurringSchedule({
            recurringAgreementNumber: this.payableInvoice.agreementId,
            period: 'day',
            periodCount: 0,
            chargeUntil: 'occurrences',
            numberOfCharges: this.payableInvoice.remainingPayments,
            firstChargeDate: firstChargeDate.toISOString(),
            firstChargeDateISO: firstChargeDate.toISOString(),
            finalChargeDate: extendedExpiryDate.toISOString(),
            finalChargeDateISO: extendedExpiryDate.toISOString(),
          });
        }
        transRequest.recurringSchedule = this.recurringSchedule;
      }

      //If the payment method is ACH, send the transaction to the ACH service. Otherwise send it to the gateway service.
      if (this.selectedPaymentMethod.cardType == 'Other') {
        response = await this.transactionService.sendACHTransaction(transRequest);
      } else {
        response = await this.transactionService.sendGatewayTransactionPayableInvoice(transRequest);
      }
      return response;
    } else {
      throw 'The payment method is not valid';
    }
  }
}
